import { useEffect, useState } from 'react';

/** Must match `top` on `.dateSeparator` in ChatContent.module.scss. */
const STICKY_TOP_PX = 8;

/**
 * Reports which day sections currently have their date separator pinned to the top.
 *
 * CSS has no `:stuck` pseudo-class, so the only way to style the pinned state is to
 * detect it. Each `.daySection` renders a 1px `[data-day-sentinel]` at the separator's
 * resting position; when that sentinel scrolls above the sticky line, the separator has
 * taken over — i.e. it is stuck.
 *
 * `groupSignature` should change whenever the set of day sections changes (e.g. older
 * messages load in a new day), so the observer re-attaches to the new sentinels.
 */
export function useStuckDayKeys(root: HTMLElement | null, groupSignature: string): Set<string> {
  const [stuck, setStuck] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    if (!root) return undefined;

    const sentinels = root.querySelectorAll<HTMLElement>('[data-day-sentinel]');
    if (!sentinels.length) {
      setStuck((prev) => (prev.size ? new Set() : prev));
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        setStuck((prev) => {
          const next = new Set(prev);
          let changed = false;

          entries.forEach((entry) => {
            const key = (entry.target as HTMLElement).dataset.daySentinel;
            if (!key) return;

            // `isIntersecting` is also false for sections still below the fold, so check
            // that the sentinel left through the *top* edge before calling it stuck.
            const rootTop = entry.rootBounds?.top ?? 0;
            const scrolledAbove = entry.boundingClientRect.top < rootTop;
            const isStuck = !entry.isIntersecting && scrolledAbove;

            if (isStuck && !next.has(key)) {
              next.add(key);
              changed = true;
            } else if (!isStuck && next.has(key)) {
              next.delete(key);
              changed = true;
            }
          });

          return changed ? next : prev; // bail out of the re-render when nothing moved
        });
      },
      { root, rootMargin: `-${STICKY_TOP_PX}px 0px 0px 0px`, threshold: 0 }
    );

    sentinels.forEach((sentinel) => observer.observe(sentinel));
    return () => observer.disconnect();
  }, [root, groupSignature]);

  return stuck;
}

export default useStuckDayKeys;
